import wrapperHOC from "@Utils/wrapperHOC";
import CustomLink from "@Components/CustomLink";
import styles from "./TrustPilotDynamicWidget.module.scss";
import React, { useRef } from "react";
import { useLazyCallback } from "hooks/useLazyCallback";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import config from "@Config/index";
import TrustPilotCustomWidget from "@Components/TrustPilotCustomWidget";

interface TrustPilotExternalWidgetProps {
  width?: string;
  height?: string;
  templateId?: string;
  margin?: string;
}

interface TrustPilotDynamicWidgetProps extends TrustPilotExternalWidgetProps {
  /** China custom widget variant: "default" (vertical), "pill" (star badge) or
   * "pillWithLogo" (star + wordmark badge) */
  chinaVariant?: "default" | "pill" | "pillWithLogo" | "footerChina";
}

/**
 * The original Trustpilot external widget (loads bootstrap script).
 * Extracted into its own component so hooks are never called conditionally.
 */
const TrustPilotExternalWidget: React.FC<TrustPilotExternalWidgetProps> = ({
  width = "240px",
  height = "116px",
  templateId = "53aa8807dec7e10d38f59f32",
  margin = "0 0 -4px 0",
}) => {
  const containerRef = useRef(null);
  const nonce = useSelector(({ appData }: AppState) => appData.nonce, shallowEqual);

  const loadScript = () => {
    if (window.Trustpilot) {
      window?.Trustpilot?.loadFromElement(containerRef.current, true);
      return;
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
    script.nonce = nonce;
    script.onload = () => {
      window?.Trustpilot?.loadFromElement(containerRef.current, true);
    };
    document.body.appendChild(script);
  };

  useLazyCallback(
    loadScript,
    {
      rootMargin: "300px 0px 300px 0px",
    },
    containerRef,
    [],
  );

  return (
    <div className={styles.container} style={{ margin, width, height }}>
      <div
        ref={containerRef}
        className="trustpilot-widget"
        data-locale="en-GB"
        data-template-id={templateId}
        data-businessunit-id="579c87e70000ff000592e82f"
        data-style-height={height}
        data-style-width={width}
        data-testid="Trustpilot-logo"
      >
        <CustomLink
          rel="noreferrer"
          href="https://uk.trustpilot.com/review/amberstudent.com"
          target="_blank"
          isExternal
        >
          {" "}
        </CustomLink>
      </div>
    </div>
  );
};

/**
 * For China builds, render the custom translatable widget instead of
 * loading the external Trustpilot script (blocked in China).
 */
const TrustPilotDynamicWidget: React.FC<TrustPilotDynamicWidgetProps> = ({
  chinaVariant = "default",
  ...props
}) => {
  if (config.IS_CHINA_APP) {
    return <TrustPilotCustomWidget variant={chinaVariant} />;
  }
  return <TrustPilotExternalWidget {...props} />;
};

export default wrapperHOC(TrustPilotDynamicWidget, {
  componentName: "TrustPilotDynamicWidget",
  showForChina: true,
});
